import { useEffect, useMemo, useState } from "react";
import { map } from "lodash";
import { IoChevronDown } from "react-icons/io5";
import { isIndianUser } from "@/utils/getCountryCode";
import styles from "./PolicyAccordion.module.css";

const BOOKING_POLICY_INDIA = [
  "<strong>Mandatory Documents:</strong> A valid passport with a minimum validity of 6 months, PAN Card, and duly completed and signed A2 Form are required for international travel, wherever applicable.",
  "<strong>Booking Confirmation:</strong> Bookings will be confirmed only upon receipt of the required documents and applicable initial payment. All bookings and services are subject to availability at the time of confirmation.",
  "<strong>Name & Details Accuracy:</strong>Clients are responsible for ensuring that all names, dates, passport details, and other information provided at the time of booking are accurate and exactly match the relevant travel documents.",
  "<strong>Payment Terms:</strong> The balance payment must be made within the timeline communicated at the time of booking. Delayed payments may result in cancellation of the booking and applicable charges.",
  "<strong>Visa & Travel Insurance:</strong> Visa processing is subject to the rules, requirements, and timelines of the concerned embassy or consulate. Travel insurance is strongly recommended and may be mandatory for certain destinations.",
  "<strong>Visa Rejection/Delay:</strong> Visa approval is solely at the discretion of the concerned authorities. In case of visa rejection or delay, applicable cancellation, non-refundable, or other charges shall be borne by the client.",
  "<strong>Amendments:</strong> Any changes requested after confirmation are subject to availability and may attract amendment charges, fare differences, or other applicable fees.",
  "<strong>Cancellation & Refunds:</strong> Cancellation charges shall be applicable as per the terms of the respective airline, hotel, service provider, or travel package. Refunds, where applicable, will be processed after receipt from the concerned service provider, after deduction of applicable charges.",
  "<strong>Price & Fare Changes:</strong> Airfares, hotel rates, taxes, visa fees, exchange rates, and other charges are subject to change until the booking is confirmed and the applicable payment is received. Any increase in cost shall be payable by the client.",
  "<strong>Travel Documents & Entry Requirements:</strong> Clients are responsible for obtaining and carrying all required visas, permits, certificates, and other documents and for complying with the entry, immigration, and health requirements of the destination.",
  "<strong>Third-Party Services:</strong> Flights, hotels, transfers, sightseeing, visas, insurance, and other travel services are subject to the respective terms and conditions of the service providers.",
  "<strong>Service Disruptions:</strong> The Company shall not be responsible for delays, cancellations, changes, or disruptions caused by airlines, hotels, visa authorities, immigration authorities, government regulations, weather conditions, strikes, or other circumstances beyond the Company’s reasonable control. Assistance will be provided, wherever reasonably possible, subject to applicable charges.",
];

const BOOKING_POLICY_NON_INDIA = [
  "<strong>Mandatory Documents:</strong> Valid Passport (minimum 6 months validity).",
  "<strong>Booking Confirmation:</strong> Bookings are confirmed only upon receipt of the initial deposit and required documents. All services are subject to availability at the time of booking.",
  "<strong>Name & Details Accuracy:</strong> It is the client’s responsibility to ensure that names and details provided match the passport and travel documents exactly.",
  "<strong>Visa & Travel Insurance:</strong> Visa processing is subject to embassy rules and timelines. Travel insurance is highly recommended and may be mandatory for certain destinations.",
  "<strong>Amendments:</strong> Any changes to the booking after confirmation may incur additional charges and are subject to availability.",
];

const PAYMENT_POLICY_INDIA = [
  "<strong>Initial Booking Amount:</strong> A minimum of 25% of the total package cost is payable at the time of booking to initiate the reservation. This amount is non-refundable and non-transferable.",
  "<strong>Second Instalment:</strong> 50% of the total package cost must be paid at least 60 days prior to the departure date.",
  "<strong>Final Payment:</strong> The remaining 25% balance must be paid in full at least 30 days prior to the departure date.",
  "<strong>Last-Minute Bookings:</strong> For bookings made within 30 days of the departure date, 100% of the total package cost is payable at the time of booking.",
  "<strong>Non-Payment:</strong> Failure to make payments within the specified timelines may result in cancellation of the booking or services, and applicable cancellation or non-refundable charges may be deducted from any amount paid.",
];

const PAYMENT_POLICY_NON_INDIA = [
  "<strong>Initial Booking Amount:</strong> A minimum of 25% of the total package cost is required at the time of booking to initiate reservations (non-refundable and non-transferable).",
  "<strong>Second Instalment:</strong> 50% of the total package cost must be paid 30 days prior to the departure date.",
  "<strong>Final Payment:</strong> The remaining 25% balance must be cleared within 30 days before departure.",
  "<strong>Last-Minute Bookings:</strong> For bookings made within 30 days of departure, 100% payment is required at the time of booking.",
  "<strong>Non-Payment Clause:</strong> Failure to make timely payments may result in cancellation of services without refund.",
];

function parsePolicy(htmlString) {
  if (!htmlString) return [];

  const html = String(htmlString);
  const liMatches = html.match(/<li[^>]*>[\s\S]*?<\/li>/gi);
  if (!liMatches?.length) return [];

  return liMatches
    .map((li) => li.replace(/^<li[^>]*>/i, "").replace(/<\/li>$/i, "").trim())
    .filter(Boolean);
}

function hasPolicyContent(conditions) {
  if (Array.isArray(conditions)) return conditions.length > 0;
  return Boolean(String(conditions || "").replace(/<[^>]*>/g, "").trim());
}

export default function PolicyAccordion({ policyData = {} }) {
  const [isIndia, setIsIndia] = useState(false);
  const [openMap, setOpenMap] = useState({});

  useEffect(() => {
    setIsIndia(isIndianUser());
  }, []);

  const formattedPolicies = useMemo(
    () => [
      {
        id: "cancellation",
        title: "Cancellation Policy",
        conditions: parsePolicy(policyData?.cancellationPolicy),
      },
      {
        id: "confirmation",
        title: "Confirmation Policy",
        conditions: parsePolicy(policyData?.confirmationPolicy),
      },
      {
        id: "refund",
        title: "Refund Policy",
        conditions: parsePolicy(policyData?.refundPolicy),
      },
      {
        id: "notes",
        title: "Notes",
        conditions: policyData?.notes?.replace(/<[^>]*>/g, "").trim() ? policyData.notes : "",
      },
      {
        id: "booking",
        title: "Booking Policy",
        conditions: isIndia ? BOOKING_POLICY_INDIA : BOOKING_POLICY_NON_INDIA,
      },
      {
        id: "payment",
        title: "Payment Policy",
        conditions: isIndia ? PAYMENT_POLICY_INDIA : PAYMENT_POLICY_NON_INDIA,
      },
    ],
    [policyData, isIndia]
  );

  const visiblePolicies = formattedPolicies.filter((policy) =>
    hasPolicyContent(policy.conditions)
  );

  const togglePolicy = (id) => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!visiblePolicies.length) return null;

  return (
    <div className={styles.wrap}>
      {map(visiblePolicies, (policy, index) => {
        const isOpen = Boolean(openMap[policy.id]);

        return (
          <div
            key={policy.id}
            className={`${styles.cardSection} ${index === 0 ? styles.cardSectionFirst : ""}`}
          >
            <button
              type="button"
              className={styles.cardHeader}
              onClick={() => togglePolicy(policy.id)}
              aria-expanded={isOpen}
            >
              <span className={styles.overviewLabel} />
              <h2 className={styles.cardTitle}>{policy.title}</h2>
              <IoChevronDown size={24} className={isOpen ? styles.chevronOpen : ""} />
            </button>

            {isOpen ? (
              <div className={styles.cardBody}>
                {Array.isArray(policy.conditions) ? (
                  <ul className={styles.policyList}>
                    {map(policy.conditions, (condition, conditionIndex) => (
                      <li
                        key={conditionIndex}
                        dangerouslySetInnerHTML={{ __html: condition }}
                      />
                    ))}
                  </ul>
                ) : (
                  <div
                    className={styles.policyHtml}
                    dangerouslySetInnerHTML={{ __html: policy.conditions }}
                  />
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
